import * as React from "react"

import { cn } from "@/lib/utils"

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card"
    className={cn(
      // Hairline depth (theme review 2026-09-09): the card is defined by its
      // 1px border and internal whitespace. No resting shadow, and no
      // shadow-on-hover, which read as a lift the design direction rejects.
      // Consistent hover across the site: the hairline picks up the navy
      // accent and the surface warms slightly. No lift, no shadow.
      "min-w-0 max-w-full rounded-lg border border-border/70 bg-card text-card-foreground hover:border-primary/40 hover:bg-muted/40",
      className,
      // Last so page-level `transition-all duration-300` cannot override it:
      // animating every property on a blurred card is what flickered.
      "transition-[border-color,background-color] duration-150 ease-out"
    )}
    {...props}
  />
))
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card-header"
    className={cn("min-w-0 flex flex-col space-y-1.5 p-[var(--portal-card-padding,1.5rem)]", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    data-slot="card-title"
    className={cn(
      "text-[length:var(--portal-card-title-size,1.5rem)] font-semibold leading-[var(--portal-card-title-leading,1)] tracking-tight",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} data-slot="card-content" className={cn("min-w-0 p-[var(--portal-card-padding,1.5rem)] pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card-footer"
    className={cn("flex items-center p-[var(--portal-card-padding,1.5rem)] pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
